<template>
  <div class="wrapper">
    <template v-if="schema.attributes.editing && !disabledForAdminOnly">
      <div class="form-inputs">
        <div v-if="file">
          <font-awesome-icon :icon="faIcons.faFile" /> {{ file }}
        </div>
        <div
          style="display: inline-block;">
          <button
            class="btn btn-link add-button"
            @click="openFileDialog">
            <font-awesome-icon class="add-button" :icon="faIcons.faPlus"/>
            <em v-show="!file">Add file</em>
            <em v-show="file">Replace file</em>
          </button>
          <button
            @click="removeFile" class="btn btn-link remove-button"
            v-if="file">
            <font-awesome-icon :icon="faIcons.faTrash" /> Remove file
        </button>
        </div>
        <div v-if="acceptedExtensions" style="font-style: italic;">
          <strong>Accepted extensions: </strong> {{ acceptedExtensions }}
        </div>
        <div
          v-if="minimumSize !== 0 ||
          maximumSize !== MAX_FILESIZE_FOR_FORM_ATTACHMENT"
          style="font-style: italic;">
          <strong>Filesize: </strong>
          {{ humanFileSize(minimumSize) }} -
          {{ humanFileSize(maximumSize) }}
        </div>
      </div>
      <div>
        <input
          class="form-control"
          :accept="acceptedExtensions"
          :class="{
            'form-input-hide': localEnv !== 'testing',
            'form-input-testing': localEnv === 'testing',
          }"
          :disabled="disabled || null"
          :id="getFieldID(schema)"
          :multiple="false"
          :readonly="schema.readonly"
          :required="schema.required"
          type="file"
          @change="setFiles($event.target.files)"
        />
      </div>
    </template>
    <template v-else>
        <div v-if="!lodash.isNull(file)">
          <font-awesome-icon :icon="faIcons.faFile" v-if="file" />
          <button
            @click.stop="downloadFormFile"
            class="btn btn-link inline"
            style="font-weight: bold;"
            v-if="file">{{ file }}</button>
          <br v-if="file">
          <div
            class="form-input-hint text-error"
            v-if="schema.attributes.editing && disabledForAdminOnly">
            Field is restricted to site administrators.
          </div>
        </div>
        <div v-else>
          <span style="font-style: italic; color: #555">
            Not available
          </span>
        </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash';
import { abstractField } from 'components/Widgets/FormGenerator';

import { MAX_FILESIZE_FOR_FORM_ATTACHMENT } from 'assets/js/api/forms';
import { CI_TESTING } from 'assets/js/api/environment';
import { MixinDataToReactiveData } from 'assets/js/util';
import { Post, urls } from 'assets/js/api';

import FieldCommon from './FieldCommon';

const localEnv = CI_TESTING ? 'testing' : process.env.NODE_ENV;

export default {
  name: 'field-q-file-upload',

  mixins: [abstractField, FieldCommon],

  setup() {
    const afd = MixinDataToReactiveData(abstractField);
    const fcd = MixinDataToReactiveData(FieldCommon);
    return {
      ...afd,
      ...fcd,
    };
  },

  computed: {
    // acceptedExtensions computes a string list of accepted extensions.
    acceptedExtensions() {
      const extensions = _.get(this, 'schema.fileUploadOptions.extensions', []);
      if (!extensions || extensions.length === 0) return '';
      return extensions.join(', ');
    },

    maximumSize() {
      return Math.min(
        _.get(this,
          'schema.fileUploadOptions.maxBytes',
          MAX_FILESIZE_FOR_FORM_ATTACHMENT),
        MAX_FILESIZE_FOR_FORM_ATTACHMENT);
    },

    minimumSize() {
      return _.get(this, 'schema.fileUploadOptions.minBytes', 0);
    },

    // file computes a file name from the file string, which is different
    // depending on whether or not we're making a new reservation or viewing
    // an existing one.
    file() {
      if (_.isArray(this.value)) return _.head(this.value);
      if (this.value && !_.isString(this.value) &&
          this.schema.attributes.editing) {
        return this.value.name;
      }
      if (_.isString(this.value) && this.schema.attributes.editing &&
          !this.formNameFromFile) {
        if (!this.value) return null;
        const name = this.value.substring(0, this.value.lastIndexOf(';'));
        return name;
      }
      if (_.isString(this.value) && !this.schema.attributes.editing) {
        const name = this.value.substring(0, this.value.lastIndexOf(';'));
        return name;
      }
      return null;
    },
  },

  data() {
    return {
      MAX_FILESIZE_FOR_FORM_ATTACHMENT,
      localEnv,
    };
  },

  methods: {
    async downloadFormFile() {
      if (!this.value || this.schema.attributes.editing) return;

      const fileId = this.value.split(';').pop();
      const params = { form_file_id: fileId };
      const url = await Post(urls.FILE_DOWNLOAD_FORM_FILE, params);
      this.downloadUrl(url);
    },

    openFileDialog() {
      const inputId = this.getFieldID(this.schema);
      document.getElementById(inputId).click();
    },

    removeFile() {
      this.value = null;
    },

    async setFiles(files) {
      const possibleFile = files?.[0];

      // This can happen if you close your file selection dialog.
      if (!possibleFile) return;

      // The accept attribute will get us pretty far but isn't universally
      // available so we check for extensions here again.
      let validFile = true;
      let invalidReason = '';
      const extensions = _.get(this, 'schema.fileUploadOptions.extensions', []);
      if (extensions.length > 0) {
        validFile = false;
        for (let extIdx = 0; extIdx < extensions.length; extIdx += 1) {
          const name = possibleFile.name.toLowerCase();
          const ext = extensions[extIdx].toLowerCase();
          validFile = validFile || _.endsWith(name, ext);
        }
        if (!validFile) {
          invalidReason = 'Extension is not in valid list: ' +
            `${this.acceptedExtensions}`;
        }
      }

      // Check for filesize within limits.
      if (possibleFile.size < this.minimumSize) {
        validFile = false;
        invalidReason = 'File is too small at ' +
          `${this.humanFileSize(possibleFile.size)}. Must be at least ` +
          `${this.humanFileSize(this.minimumSize)}.`;
      }
      if (possibleFile.size > this.maximumSize) {
        validFile = false;
        invalidReason = 'File is too large at ' +
          `${this.humanFileSize(possibleFile.size)}. Must be at most ` +
          `${this.humanFileSize(this.maximumSize)}.`;
      }

      if (!validFile) {
        await this.$swal({
          title: 'Cannot add file',
          text: invalidReason,
          type: 'warning',
        });
      } else {
        this.value = possibleFile;
      }
    },
  },
};
</script>

<style lang="scss">
  @import '~assets/css/colors';

  .response {
    &.empty-value {
      font-weight: normal;
      color: $greycolor;
    }
  }

  .vue-form-generator .field-input {
    .wrapper {
      width: 100%;
    }
    .helper {
      margin: auto 0.5em;
    }
  }

  button.btn.btn-link.add-button {
    color: $successcolor;
    cursor: pointer;
  }

  button.btn.btn-link.remove-button {
    color: $primarycolor;
    cursor: pointer;
    margin-left: 1em;
  }

  .form-input-hide {
    display: none;
  }

  .form-input-testing {
    height: 30px;
    width: 100px;
    background: transparent;
    border: none;
  }
</style>
